import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button, Pagination } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Badge from "../../../componentes/ui/Badge";
import communityAPI from "../../../api/community";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
import { LearningPathActions } from "../shared/CRUDActions";
import { LearningPathEdit, LearningPathCreate } from "./LearningPathManager";
import { FullscreenSpinner } from '../../../componentes/LoadingIndicator';
import { formatDate } from "../../../utils/dateFormat";
import { formatNumber } from "../../../utils/numberFormat";
import { useLearningPaths } from "../../../hooks/useCommunity";

export default function LearningList({ isOwn = false }) {
  const { user } = useCommunityAuth();
  const navigate = useNavigate();
  const [pathProgress, setPathProgress] = useState({});
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);

  // Management state
  const [editingPath, setEditingPath] = useState(null);
  const [creating, setCreating] = useState(false);
  const [levelStats, setLevelStats] = useState([]);
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState("all");

  const apiLevelValues = useMemo(() => {
    const values = new Set();
    (levelStats || []).forEach((item) => {
      const value = (item.level || "").toLowerCase();
      if (value) {
        values.add(value);
      }
    });
    return values;
  }, [levelStats]);

  const filters = useMemo(() => {
    const params = {};
    if (isOwn) {
      params.mine = true;
    }
    if (selectedLevel !== "all" && apiLevelValues.has(selectedLevel)) {
      params.level = selectedLevel;
    }
    return params;
  }, [isOwn, selectedLevel, apiLevelValues]);

  const {
    learningPaths,
    loading: learningPathsLoading,
    fetchLearningPaths,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
  } = useLearningPaths(filters, { paginated: true, pageSize: 9 });

  const loadLevelStats = useCallback(async () => {
    setLevelsLoading(true);
    try {
      const response = await communityAPI.learningPaths.getLevels({
        ...(isOwn ? { mine: true } : {}),
      });
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setLevelStats(data);
    } catch (err) {
      console.error("Failed to load level stats", err);
      setLevelStats([]);
    } finally {
      setLevelsLoading(false);
    }
  }, [isOwn]);

  useEffect(() => {
    loadLevelStats();
  }, [loadLevelStats]);

  useEffect(() => {
    setPage(1);
  }, [selectedLevel, setPage]);

  const fallbackLevels = useMemo(() => {
    const map = new Map();
    (learningPaths || []).forEach((path) => {
      const value = (path.level || "unknown").toLowerCase();
      if (!map.has(value)) {
        map.set(value, { value, count: null });
      }
    });
    return Array.from(map.values());
  }, [learningPaths]);

  const formatLevelLabel = (value) => {
    if (!value || value === "all") {
      return "All Levels";
    }
    if (value === "unknown") {
      return "Unknown";
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const levelOptions = useMemo(() => {
    const options = [
      { label: "All Levels", value: "all", count: totalCount },
    ];
    const seen = new Set(options.map((option) => option.value));

    (levelStats || []).forEach((item) => {
      const value = (item.level || "").toLowerCase();
      if (!value || seen.has(value)) {
        return;
      }
      const count = item.count ?? item.total ?? 0;
      if (count <= 0) {
        return;
      }
      options.push({
        label: formatLevelLabel(value),
        value,
        count,
      });
      seen.add(value);
    });

    fallbackLevels.forEach((option) => {
      const value = option.value;
      if (!value || seen.has(value)) {
        return;
      }
      options.push({
        label: formatLevelLabel(value),
        value,
        count: option.count,
      });
      seen.add(value);
    });

    return options;
  }, [levelStats, fallbackLevels, totalCount]);

  const levelOptionMap = useMemo(() => {
    const map = new Map();
    levelOptions.forEach((option) => {
      map.set(option.value, option);
    });
    return map;
  }, [levelOptions]);

  useEffect(() => {
    if (selectedLevel !== "all" && !levelOptionMap.has(selectedLevel)) {
      setSelectedLevel("all");
    }
  }, [selectedLevel, levelOptionMap]);

  const filteredPaths = useMemo(() => {
    if (!Array.isArray(learningPaths)) {
      return [];
    }
    if (selectedLevel === "all") {
      return learningPaths;
    }
    if (apiLevelValues.has(selectedLevel)) {
      return learningPaths;
    }
    return learningPaths.filter(
      (path) => (path.level || "unknown").toLowerCase() === selectedLevel
    );
  }, [learningPaths, selectedLevel, apiLevelValues]);

  const progressTargets = useMemo(() => {
    if (!Array.isArray(filteredPaths)) {
      return [];
    }
    return filteredPaths.map((path) => ({
      id: path.id,
      total: path.posts?.length || 0,
    }));
  }, [filteredPaths]);

  const progressKey = useMemo(() => {
    if (progressTargets.length === 0) {
      return "";
    }
    return progressTargets
      .map(({ id, total }) => `${id || "unknown"}:${total}`)
      .join("|");
  }, [progressTargets]);

  const refreshLearningPaths = useCallback(
    async (targetPage = page) => {
      try {
        await fetchLearningPaths({ force: true, silent: true, page: targetPage });
      } catch (err) {
        console.error("Failed to refresh learning paths", err);
      }
      await loadLevelStats();
    },
    [fetchLearningPaths, loadLevelStats, page]
  );

  const handlePathDeleted = async () => {
    const currentList = filteredPaths;
    const shouldGoBack = currentList.length === 1 && page > 1;
    const targetPage = shouldGoBack ? page - 1 : page;
    if (shouldGoBack) {
      setPage(targetPage);
    }
    await refreshLearningPaths(targetPage);
  };

  const progressTargetsRef = useRef(progressTargets);

  useEffect(() => {
    progressTargetsRef.current = progressTargets;
  }, [progressTargets]);

  useEffect(() => {
    let isMounted = true;
    const loadProgress = async () => {
      if (!user) {
        if (isMounted) {
          setPathProgress({});
        }
        return;
      }
      const targets = progressTargetsRef.current;
      if (!targets.length) {
        if (isMounted) {
          setPathProgress({});
        }
        return;
      }
      const progressData = {};
      for (const target of targets) {
        const pathId = target.id;
        try {
          const progressRes = await communityAPI.progress.getCompletedLessons(pathId);
          const completedLessons = progressRes.data?.completed_lessons || [];
          const completedCount = completedLessons.length;
          const totalLessons = target.total || 0;
          progressData[pathId] = {
            completed: completedCount,
            total: totalLessons,
          };
        } catch {
          const stored = localStorage.getItem(`completed_${pathId}`);
          const completed = stored ? JSON.parse(stored) : [];
          progressData[pathId] = {
            completed: completed.length,
            total: target.total || 0,
          };
        }
      }
      if (isMounted) {
        setPathProgress(progressData);
      }
    };
    loadProgress();
    return () => {
      isMounted = false;
    };
  }, [user, progressKey]);

  const selectedLevelLabel =
    levelOptionMap.get(selectedLevel)?.label || formatLevelLabel(selectedLevel);

  const paginationTotal =
    selectedLevel === "all" || apiLevelValues.has(selectedLevel)
      ? totalCount ?? 0
      : filteredPaths.length;

  const selectedLevelCount =
    levelOptionMap.get(selectedLevel)?.count ??
    (selectedLevel === "all" ? totalCount : filteredPaths.length);

  const getLearningLink = (path) => {
    if (!path) return "/learning";
    return path.slug ? `/learning/${path.slug}` : `/learning/${path.id}`;
  };

  if (learningPathsLoading && filteredPaths.length === 0) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <FullscreenSpinner message="Loading learning paths..." tone="slate" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@400;500;600&display=swap');
        .blog-title { font-family: 'Crimson Pro', Georgia, serif; }
        .blog-body { font-family: 'DM Sans', sans-serif; }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
          <div>
            <h1 className="blog-title text-4xl lg:text-5xl font-bold text-gray-950 dark:text-white mb-3">
              {isOwn ? "My Learning Paths" : "Learning Paths"}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">
              {isOwn
                ? "Manage your created learning paths."
                : "Explore curated paths created by the community to master new security and development skills."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative w-full sm:w-64">
              <button
                onClick={() => setIsLevelDropdownOpen(!isLevelDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-[#161616] rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex flex-col text-left">
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {selectedLevelLabel}
                  </span>
                  {selectedLevelCount != null && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatNumber(selectedLevelCount)} paths
                    </span>
                  )}
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isLevelDropdownOpen ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isLevelDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40 bg-black/5"
                    onClick={() => setIsLevelDropdownOpen(false)}
                  />

                  <div className="absolute top-full left-0 right-0 z-50 mt-2 bg-white dark:bg-[#161616] rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
                    <div className="max-h-64 overflow-y-auto py-1">
                      {levelsLoading && (
                        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          Loading levels...
                        </div>
                      )}
                      {levelOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSelectedLevel(option.value);
                            setIsLevelDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 transition-all duration-150 group ${selectedLevel === option.value
                              ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary/80 border-l-4 border-l-indigo-600"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            }`}
                        >
                          <span className="font-medium">{option.label}</span>
                          {typeof option.count === "number" && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formatNumber(option.count)}
                            </span>
                          )}
                        </button>
                      ))}
                      {!levelsLoading && levelOptions.length === 0 && (
                        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          No levels available
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {user && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreating(true)}
                className="h-full py-3 px-6 rounded-xl bg-primary hover:bg-[#229bb8] text-white font-semibold shadow-lg"
              >
                Add Learning
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPaths.length === 0 ? (
            <div className="col-span-full text-center py-16 text-gray-600 dark:text-gray-400">
              No learning paths available
            </div>
          ) : (
            filteredPaths.map((path) => {
              const progress = pathProgress[path.id];
              const progressPercent =
                progress && progress.total > 0
                  ? Math.round((progress.completed / progress.total) * 100)
                  : null;

              return (
                <Link
                  key={path.id}
                  to={getLearningLink(path)}
                  className="group block"
                >
                  <div className="bg-white/90 dark:bg-[#161616]/90 backdrop-blur-sm p-6 rounded-3xl shadow-lg border border-white/20 dark:border-gray-700/40 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden h-full flex flex-col">
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />

                    {user && user.id === path.author?.id && (
                      <div className="absolute top-3 right-4 z-10" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <LearningPathActions
                          learningPath={path}
                          onEdit={() => setEditingPath(path)}
                          onDeleted={handlePathDeleted}
                        />
                      </div>
                    )}

                    <div className="relative flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <div className="pr-8">
                          {path.is_approved === false && (
                            <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-500/10 px-2.5 py-0.5 mb-2 text-xs font-semibold text-amber-700 dark:text-amber-100">
                              Pending admin review
                            </span>
                          )}
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary/80 transition-colors line-clamp-2">
                              {path.title}
                            </h3>

                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {formatNumber(path.posts?.length || 0)} lessons
                          </p>
                        </div>
                        <Badge
                          color={
                            path.level === "beginner"
                              ? "green"
                              : path.level === "intermediate"
                                ? "yellow"
                                : "red"
                          }
                          className="uppercase"
                        >
                          {path.level || "Unknown"}
                        </Badge>
                      </div>

                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3 flex-1">
                        {path.description || "No description provided yet."}
                      </p>

                      {progressPercent !== null && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Your Progress
                            </span>
                            <span className="text-xs font-semibold text-primary dark:text-primary/80">
                              {progressPercent}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-500"
                              style={{ width: `${progressPercent}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatNumber(progress.completed)} of {formatNumber(progress.total)} completed
                          </p>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>by {path.author?.username ? (
                            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/profile/${path.author.username}`); }} className="hover:text-primary dark:hover:text-primary/80 transition-colors cursor-pointer">
                              {path.author.username}
                            </span>
                          ) : (
                            "Anonymous"
                          )}</span>
                          <span>•</span>
                          <span>
                            {formatDate(path.created_at)}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-primary dark:text-primary/80 group-hover:translate-x-1 transition-transform">
                          View Path →
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>

        {paginationTotal > 0 && filteredPaths.length > 0 && (
          <div className="mt-10 flex justify-center">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={paginationTotal}
              showSizeChanger
              pageSizeOptions={["6", "9", "12", "18"]}
              onChange={(nextPage, nextPageSize) => {
                if (nextPageSize !== pageSize) {
                  setPageSize(nextPageSize);
                }
                if (nextPage !== page) {
                  setPage(nextPage);
                }
              }}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${formatNumber(total)} paths`
              }
              className="antd-pagination"
            />
          </div>
        )}

        {!isOwn && (
          <div className="mt-16 rounded-3xl border border-indigo-100 dark:border-indigo-900/40 bg-gradient-to-r from-primary/5 via-white to-secondary/5 dark:from-indigo-900/20 dark:via-gray-900 dark:to-purple-900/20 p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Share your expertise
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Build a learning path to guide others through the concepts you
                know best.
              </p>
            </div>
            <Link
              to="/community/learning"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary hover:bg-[#229bb8] text-white font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              My Learning Paths
            </Link>
          </div>
        )}
      </div>

      <Modal
        className="[&_.ant-modal-content]:dark:bg-[#161616] [&_.ant-modal-content]:rounded-2xl [&_.ant-modal-header]:dark:bg-[#161616] [&_.ant-modal-header]:dark:border-gray-800 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-title]:text-xl [&_.ant-modal-title]:font-bold [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-[#161616] [&_.ant-modal-footer]:dark:border-gray-800"
        style={{ top: 24, paddingBottom: 24, maxWidth: '95vw' }}
        open={!!editingPath}
        onCancel={() => setEditingPath(null)}
        footer={null}
        title="Edit Learning Path"
        width={1000}
      >
        {editingPath && (
          <LearningPathEdit
            id={editingPath.id}
            onSave={async () => {
              setEditingPath(null);
              await refreshLearningPaths();
            }}
            onCancel={() => setEditingPath(null)}
          />
        )}
      </Modal>

      {/* Create Learning Path Modal */}
      <Modal
        className="[&_.ant-modal-content]:dark:bg-[#161616] [&_.ant-modal-content]:rounded-2xl [&_.ant-modal-header]:dark:bg-[#161616] [&_.ant-modal-header]:dark:border-gray-800 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-title]:text-xl [&_.ant-modal-title]:font-bold [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-[#161616] [&_.ant-modal-footer]:dark:border-gray-800"
        style={{ top: 24, paddingBottom: 24, maxWidth: '95vw' }}
        open={creating}
        onCancel={() => setCreating(false)}
        footer={null}
        title="Create Learning Path"
        width={1000}
      >
        {creating && (
          <LearningPathCreate
            onSave={async () => {
              setCreating(false);
              setPage(1);
              await refreshLearningPaths(1);
            }}
            onCancel={() => setCreating(false)}
          />
        )}
      </Modal>
    </div>
  );
}
